import React, { useState } from 'react';
import { PRODUCTION_MYSQL_SCHEMA } from '../../data/productionSchemaSql';
import {
  Database,
  Copy,
  Check,
  Download,
  Terminal,
  Server,
  Layers,
  Key,
  Search,
  ExternalLink,
  TableProperties,
  Cpu,
  FileCode2,
  HardDrive,
  RefreshCw
} from 'lucide-react';

const TABLES_METADATA = [
  {
    name: 'products',
    description: 'Core machinery catalog with KSh pricing, food-grade SS304 specs, features JSON, and SEO metadata.',
    primaryKey: 'id (VARCHAR 80, e.g. milk-pasteurizer-300l)',
    indexes: 'idx_prod_category, idx_prod_stock, idx_prod_price',
    relations: 'Referenced by inquiries and quotes',
    recordsEstimated: 'Catalog units'
  },
  {
    name: 'blog_posts',
    description: 'Technical articles, Kenyan dairy economics, borehole water science case studies, JSON sections and takeaways.',
    primaryKey: 'id (VARCHAR 80), slug (UNIQUE)',
    indexes: 'slug, idx_blog_category, idx_blog_featured',
    relations: 'Parent to reader comments and related equipment links',
    recordsEstimated: 'Published articles'
  },
  {
    name: 'leads_rfq',
    description: 'Customer requests for quotations (RFQs), phone, email, Kenyan county, and itemized machinery list.',
    primaryKey: 'id (VARCHAR 64, e.g. RFQ-894102)',
    indexes: 'idx_lead_status, idx_lead_county',
    relations: 'Customer inquiries',
    recordsEstimated: 'Buyer quotations'
  },
  {
    name: 'media_library',
    description: 'Uploaded high-resolution equipment photography, workshop assets, and category tags stored as LONGTEXT data/URLs.',
    primaryKey: 'id (VARCHAR 80)',
    indexes: 'PRIMARY KEY',
    relations: 'Used in products and articles',
    recordsEstimated: 'Media gallery'
  },
  {
    name: 'admins',
    description: 'System administrators and engineering staff with authentication credentials and role permissions.',
    primaryKey: 'id (INT UNSIGNED)',
    indexes: 'idx_admin_email',
    relations: 'Administrative audit',
    recordsEstimated: 'Admin users'
  }
];

export const AdminDatabaseSchema: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSubTab, setActiveSubTab] = useState<'sql' | 'tables' | 'deployment'>('sql');

  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [downloadNotice, setDownloadNotice] = useState<string | null>(null);
  const [copiedZipUrl, setCopiedZipUrl] = useState(false);

  const handleCopySql = () => {
    navigator.clipboard.writeText(PRODUCTION_MYSQL_SCHEMA);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleDownloadSql = () => {
    const blob = new Blob([PRODUCTION_MYSQL_SCHEMA], { type: 'text/sql;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `saintstech_production_schema_${new Date().toISOString().slice(0, 10)}.sql`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCopyZipUrl = () => {
    const fullUrl = `${window.location.origin}/api/download/cpanel-build`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedZipUrl(true);
    setTimeout(() => setCopiedZipUrl(false), 2500);
  };

  const handleDownloadZip = async () => {
    if (isDownloadingZip) return;
    setIsDownloadingZip(true);
    setDownloadNotice('Streaming zip archive (32 MB)...');

    try {
      const res = await fetch('/api/download/cpanel-build');
      if (!res.ok) {
        throw new Error(`Server response error: ${res.status}`);
      }

      setDownloadNotice('Saving zip to your computer...');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'saints-tech-solutions-cpanel-production.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 4000);

      setDownloadNotice('Download complete!');
      setTimeout(() => setDownloadNotice(null), 3000);
    } catch (err) {
      console.warn('In-app blob download fallback:', err);
      setDownloadNotice('Opening download in new tab...');
      const fallbackLink = document.createElement('a');
      fallbackLink.href = '/api/download/cpanel-build';
      fallbackLink.target = '_blank';
      fallbackLink.rel = 'noreferrer';
      document.body.appendChild(fallbackLink);
      fallbackLink.click();
      document.body.removeChild(fallbackLink);
      setTimeout(() => setDownloadNotice(null), 3000);
    } finally {
      setIsDownloadingZip(false);
    }
  };

  const filteredTables = TABLES_METADATA.filter(
    (t) =>
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.relations.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-slate-950/80 p-5 rounded-2xl border border-slate-800">
        <div>
          <h2 className="text-xl font-extrabold text-white tracking-tight flex items-center gap-2.5">
            <Database className="w-5 h-5 text-sky-400" />
            <span>Production MySQL Database Schema (v1.0.0)</span>
          </h2>
          <p className="text-xs text-slate-400 mt-1 max-w-2xl">
            Complete enterprise relational database architecture for MySQL 8.0+, AWS Aurora, and Google Cloud SQL. Designed with InnoDB engine, utf8mb4 collation, Full-Text indexing, and strict foreign keys.
          </p>
        </div>

        <div className="flex items-center gap-2.5 shrink-0">
          <button
            type="button"
            onClick={handleCopySql}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white rounded-xl text-xs font-semibold border border-slate-700 transition-colors cursor-pointer"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400">Copied to Clipboard!</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 text-slate-400" />
                <span>Copy SQL Script</span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handleDownloadSql}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold border border-slate-700 transition-colors cursor-pointer"
          >
            <Download className="w-4 h-4 text-sky-400" />
            <span>Download .sql File</span>
          </button>

          <button
            type="button"
            disabled={isDownloadingZip}
            onClick={handleDownloadZip}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-950/40 transition-all cursor-pointer disabled:opacity-75"
          >
            {isDownloadingZip ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>{downloadNotice || 'Downloading...'}</span>
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                <span>Download cPanel Package (.zip)</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Total Tables</div>
          <div className="text-2xl font-extrabold text-white tabular-nums mt-1">15 Core Tables</div>
          <div className="text-[11px] text-slate-500 mt-0.5">+ 2 Stored Views</div>
        </div>

        <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
          <div className="text-[11px] font-bold uppercase tracking-wider text-sky-400">Storage Engine</div>
          <div className="text-2xl font-extrabold text-sky-400 mt-1">InnoDB</div>
          <div className="text-[11px] text-slate-500 mt-0.5">ACID compliant transactions</div>
        </div>

        <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
          <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-400">Character Encoding</div>
          <div className="text-2xl font-extrabold text-emerald-400 mt-1">utf8mb4</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Collation: unicode_ci</div>
        </div>

        <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800">
          <div className="text-[11px] font-bold uppercase tracking-wider text-amber-400">Search Capabilities</div>
          <div className="text-2xl font-extrabold text-amber-400 mt-1">FULLTEXT</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Natural language catalog search</div>
        </div>
      </div>

      {/* Sub Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-800 pb-2">
        <button
          type="button"
          onClick={() => setActiveSubTab('sql')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-colors cursor-pointer ${
            activeSubTab === 'sql'
              ? 'bg-sky-600 text-white shadow-sm'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
          }`}
        >
          <FileCode2 className="w-4 h-4" />
          <span>production_schema.sql Code</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveSubTab('tables')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-colors cursor-pointer ${
            activeSubTab === 'tables'
              ? 'bg-sky-600 text-white shadow-sm'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
          }`}
        >
          <TableProperties className="w-4 h-4" />
          <span>Tables & Foreign Keys Architecture ({TABLES_METADATA.length})</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveSubTab('deployment')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-colors cursor-pointer ${
            activeSubTab === 'deployment'
              ? 'bg-sky-600 text-white shadow-sm'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900'
          }`}
        >
          <Terminal className="w-4 h-4" />
          <span>Deployment & Connection Guide</span>
        </button>
      </div>

      {/* SUBTAB 1: SQL VIEWER */}
      {activeSubTab === 'sql' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>Showing raw SQL DDL file ready for MySQL CLI / Workbench / Cloud SQL execution:</span>
            <span className="font-mono text-slate-500">database/production_schema.sql (14.2 KB)</span>
          </div>

          <div className="relative bg-slate-950 rounded-2xl border border-slate-800 overflow-hidden shadow-2xl">
            <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900/90 border-b border-slate-800 text-[11px] text-slate-400 font-mono">
              <span className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500 inline-block" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
                <span className="ml-2 text-slate-300 font-bold">production_schema.sql</span>
              </span>
              <button
                type="button"
                onClick={handleCopySql}
                className="text-xs text-sky-400 hover:text-sky-300 flex items-center gap-1 cursor-pointer"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copied ? 'Copied' : 'Copy'}</span>
              </button>
            </div>

            <pre className="p-4 sm:p-6 text-xs text-slate-200 font-mono overflow-x-auto max-h-[600px] leading-relaxed scrollbar-thin">
              <code>{PRODUCTION_MYSQL_SCHEMA}</code>
            </pre>
          </div>
        </div>
      )}

      {/* SUBTAB 2: TABLES ARCHITECTURE */}
      {activeSubTab === 'tables' && (
        <div className="space-y-4">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search schema tables by name, purpose, or relations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full text-xs pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:outline-hidden focus:ring-2 focus:ring-sky-500"
            />
          </div>

          <div className="bg-slate-950/80 rounded-2xl border border-slate-800 overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="bg-slate-900/90 border-b border-slate-800 text-slate-400 font-bold uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="py-3.5 px-4">Table Name</th>
                    <th className="py-3.5 px-4">Description & Role</th>
                    <th className="py-3.5 px-4">Primary Key</th>
                    <th className="py-3.5 px-4">Indexes & Search</th>
                    <th className="py-3.5 px-4">Foreign Keys</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-850 text-slate-300">
                  {filteredTables.map((t) => (
                    <tr key={t.name} className="hover:bg-slate-900/40 transition-colors">
                      <td className="py-3.5 px-4 font-mono font-bold text-sky-400 whitespace-nowrap">
                        `{t.name}`
                      </td>
                      <td className="py-3.5 px-4 text-slate-300 max-w-xs">{t.description}</td>
                      <td className="py-3.5 px-4 font-mono text-emerald-400 text-[11px] whitespace-nowrap">
                        {t.primaryKey}
                      </td>
                      <td className="py-3.5 px-4 font-mono text-slate-400 text-[11px]">{t.indexes}</td>
                      <td className="py-3.5 px-4 font-mono text-amber-400 text-[11px]">{t.relations}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* SUBTAB 3: DEPLOYMENT GUIDE */}
      {activeSubTab === 'deployment' && (
        <div className="space-y-6 text-xs text-slate-300">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* cPanel Deployment Card */}
            <div className="md:col-span-2 p-5 bg-gradient-to-br from-emerald-950/50 via-slate-950 to-slate-900 rounded-2xl border border-emerald-500/30 space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 text-white font-bold text-sm">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-extrabold text-xs">
                    ZIP
                  </div>
                  <div>
                    <div className="text-emerald-400 font-extrabold">cPanel Hosted Server Production Deployment</div>
                    <div className="text-[11px] text-slate-400 font-normal">Complete stripped package without development files (32.4 MB)</div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    disabled={isDownloadingZip}
                    onClick={handleDownloadZip}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-950/40 transition-colors cursor-pointer disabled:opacity-75"
                  >
                    {isDownloadingZip ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>{downloadNotice || 'Downloading...'}</span>
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        <span>Download cPanel Zip Archive</span>
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleCopyZipUrl}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-900 hover:bg-slate-800 text-slate-300 rounded-xl text-xs font-semibold border border-slate-700 transition-colors cursor-pointer"
                  >
                    {copiedZipUrl ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="text-emerald-400">URL Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        <span>Copy Direct URL</span>
                      </>
                    )}
                  </button>

                  <a
                    href="/api/download/cpanel-build"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2.5 py-2 text-sky-400 hover:text-sky-300 text-xs font-semibold hover:underline"
                    title="Open in new window"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>New Tab</span>
                  </a>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 text-[11px]">
                <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-800">
                  <div className="font-bold text-white mb-1">1. Upload & Extract</div>
                  <p className="text-slate-400">Upload the zip file via cPanel File Manager into your root or public_html directory and extract.</p>
                </div>
                <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-800">
                  <div className="font-bold text-white mb-1">2. Import in phpMyAdmin</div>
                  <p className="text-slate-400">Create a MySQL database in cPanel and import <code className="text-sky-300">database/production_schema.sql</code>.</p>
                </div>
                <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-800">
                  <div className="font-bold text-white mb-1">3. Setup Node.js App</div>
                  <p className="text-slate-400">Select Node 20.x, set startup file to <code className="text-sky-300">server.js</code>, configure .env, run npm install, and start.</p>
                </div>
              </div>
            </div>

            {/* Quick Import 1: CLI */}
            <div className="p-5 bg-slate-950 rounded-2xl border border-slate-800 space-y-3">
              <div className="flex items-center gap-2 text-white font-bold text-sm">
                <Terminal className="w-4 h-4 text-sky-400" />
                <span>1. MySQL CLI / Local Server Import</span>
              </div>
              <p className="text-slate-400 leading-relaxed">
                Run the script directly from your terminal or SSH session to bootstrap the database and sample seed records:
              </p>
              <pre className="p-3 bg-slate-900 rounded-xl text-emerald-400 font-mono overflow-x-auto">
{`# Create database and import all tables
mysql -u root -p < database/production_schema.sql

# Or connect to remote host:
mysql -h your-mysql-host.com -u saintstech_user -p saintstech_db < database/production_schema.sql`}
              </pre>
            </div>

            {/* Quick Import 2: Docker */}
            <div className="p-5 bg-slate-950 rounded-2xl border border-slate-800 space-y-3">
              <div className="flex items-center gap-2 text-white font-bold text-sm">
                <Server className="w-4 h-4 text-amber-400" />
                <span>2. Docker & Compose Setup</span>
              </div>
              <p className="text-slate-400 leading-relaxed">
                Mount the schema into Docker's init directory for instant automated startup:
              </p>
              <pre className="p-3 bg-slate-900 rounded-xl text-amber-400 font-mono overflow-x-auto">
{`docker run -d --name saintstech-db \\
  -e MYSQL_ROOT_PASSWORD=secret_pass \\
  -e MYSQL_DATABASE=saintstech_db \\
  -v $(pwd)/database/production_schema.sql:/docker-entrypoint-initdb.d/01_schema.sql \\
  -p 3306:3306 \\
  mysql:8.0`}
              </pre>
            </div>

            {/* Quick Import 3: Node.js Pool */}
            <div className="p-5 bg-slate-950 rounded-2xl border border-slate-800 space-y-3">
              <div className="flex items-center gap-2 text-white font-bold text-sm">
                <Cpu className="w-4 h-4 text-indigo-400" />
                <span>3. Node.js / Express mysql2 Pool</span>
              </div>
              <p className="text-slate-400 leading-relaxed">
                Connect your backend using high-performance connection pooling:
              </p>
              <pre className="p-3 bg-slate-900 rounded-xl text-indigo-300 font-mono overflow-x-auto">
{`import mysql from 'mysql2/promise';

export const pool = mysql.createPool({
  host: process.env.DB_HOST || 'localhost',
  user: process.env.DB_USER || 'saintstech_user',
  password: process.env.DB_PASSWORD,
  database: 'saintstech_db',
  waitForConnections: true,
  connectionLimit: 15,
  queueLimit: 0
});`}
              </pre>
            </div>

            {/* Quick Import 4: AWS RDS / Cloud SQL */}
            <div className="p-5 bg-slate-950 rounded-2xl border border-slate-800 space-y-3">
              <div className="flex items-center gap-2 text-white font-bold text-sm">
                <HardDrive className="w-4 h-4 text-rose-400" />
                <span>4. AWS RDS & Google Cloud SQL</span>
              </div>
              <p className="text-slate-400 leading-relaxed">
                Optimized parameters for managed cloud database instances:
              </p>
              <ul className="space-y-1.5 list-disc list-inside text-slate-400">
                <li><strong className="text-slate-200">DB Instance:</strong> MySQL 8.0.35+ or MariaDB 10.11</li>
                <li><strong className="text-slate-200">Parameter Group:</strong> <code className="text-sky-300">character_set_server = utf8mb4</code></li>
                <li><strong className="text-slate-200">Collation:</strong> <code className="text-sky-300">collation_server = utf8mb4_unicode_ci</code></li>
                <li><strong className="text-slate-200">Timezone:</strong> <code className="text-sky-300">Africa/Nairobi (UTC+3)</code></li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
